import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaCreditCard, FaCheckCircle, FaLock } from "react-icons/fa";

const planPrices: Record<string, number> = {
  small: 250,
  medium: 500,
  "large-medium": 700,
  large: 900,
};
const maintenancePrices: Record<string, number> = {
  small: 75,
  medium: 100,
  "large-medium": 125,
  large: 150,
};
const supportPrices: Record<string, number> = {
  small: 25,
  medium: 50,
  "large-medium": 75,
  large: 100,
};
const planNames: Record<string, string> = {
  small: "Small Plan (4–50 beds)",
  medium: "Medium Plan (51–80 beds)",
  "large-medium": "Large-Medium Plan (81–125 beds)",
  large: "Large Plan (126+ beds)",
};

const FreeTrialPayment: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as any) || {};
  const plan = state.plan || "small";
  const [maintenance, setMaintenance] = useState(true);
  const [support, setSupport] = useState(true);
  const [card, setCard] = useState({ number: "", expiry: "", cvc: "" });
  const [error, setError] = useState("");

  const base = planPrices[plan] || 250;
  const maintenanceFee = maintenance ? maintenancePrices[plan] : 0;
  const supportFee = support ? supportPrices[plan] : 0;
  const total = base + maintenanceFee + supportFee;

  const handleCardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCard({ ...card, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!card.number || !card.expiry || !card.cvc) {
      setError("Please enter all credit card fields.");
      return;
    }
    setError("");
    navigate("/onboarding", {
      state: {
        ...state,
        plan,
        maintenance,
        support,
        total,
      },
    });
  };

  return (
    <motion.section initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, type: "spring" }} className="max-w-xl mx-auto py-12 px-4">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.1, type: "spring" }} className="bg-gradient-to-r from-blue-100 to-blue-50 rounded-3xl shadow-lg p-8 mb-8 text-center">
        <h2 className="text-3xl font-extrabold text-blue-800 mb-2">Confirm Your Plan & Add-ons</h2>
        <div className="mb-2 text-blue-700 font-bold text-lg">{planNames[plan]}</div>
        <div className="text-gray-700 mb-2">Base Price: <span className="font-semibold">${base}/mo</span></div>
        <div className="text-gray-700 mb-2">Name: <span className="font-semibold">{state.firstName} {state.lastName}</span></div>
        <div className="text-gray-700 mb-2">Facility: <span className="font-semibold">{state.facility}</span></div>
        <div className="text-gray-700 mb-2">Location: <span className="font-semibold">{state.location}</span></div>
      </motion.div>
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow p-8 space-y-6">
        <div className="flex flex-col gap-4 mb-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={maintenance} onChange={() => setMaintenance((v) => !v)} className="accent-blue-600 w-5 h-5" />
            <span className="text-blue-800 font-semibold">Maintenance Module</span>
            <span className="ml-auto text-gray-600">+${maintenancePrices[plan]}/mo</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={support} onChange={() => setSupport((v) => !v)} className="accent-blue-600 w-5 h-5" />
            <span className="text-blue-800 font-semibold">Support Package</span>
            <span className="ml-auto text-gray-600">+${supportPrices[plan]}/mo</span>
          </label>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 flex items-center justify-between mb-4">
          <span className="font-bold text-blue-800">Total after trial:</span>
          <span className="text-2xl font-extrabold text-blue-700">${total}/mo</span>
        </div>
        <div className="text-blue-700 text-sm mb-2 flex items-center gap-2"><FaLock /> No charge today. Your card will be charged after your 14-day free trial.</div>
        <div className="grid grid-cols-1 gap-4 mb-2">
          <div>
            <label className="block text-blue-800 font-semibold mb-1">Card Number</label>
            <div className="relative">
              <FaCreditCard className="absolute left-3 top-3 text-blue-300" />
              <input type="text" name="number" value={card.number} onChange={handleCardChange} className="w-full border rounded pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-200" placeholder="1234 5678 9012 3456" required />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-1/2">
              <label className="block text-blue-800 font-semibold mb-1">Expiry</label>
              <input type="text" name="expiry" value={card.expiry} onChange={handleCardChange} className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-200" placeholder="MM/YY" required />
            </div>
            <div className="w-1/2">
              <label className="block text-blue-800 font-semibold mb-1">CVC</label>
              <input type="text" name="cvc" value={card.cvc} onChange={handleCardChange} className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-200" placeholder="123" required />
            </div>
          </div>
        </div>
        {error && <div className="text-red-600 text-sm text-center">{error}</div>}
        <motion.button whileTap={{ scale: 0.97 }} whileHover={{ scale: 1.04 }} type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-3 rounded-lg shadow-lg font-bold text-lg flex items-center justify-center gap-2 mt-2">
          Start Free Trial <FaCheckCircle />
        </motion.button>
      </form>
    </motion.section>
  );
};

export default FreeTrialPayment; 